import {
  Button,
  Divider,
  Flex,
  Icons,
  Image,
  Spin,
  theme,
  Typography,
} from '@contiple/antd-ds';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useReceiptQuery } from '@/hooks/apis/billing/query';
import { ReceiptErrorCode, type ExtraCharge } from '@/types/billing';
import {
  formatCurrency,
  formatPaymentItemName,
  getPaymentMethodLabel,
} from '../utils';

const { Text } = Typography;

function ReceiptRow({
  label,
  value,
  indent,
}: {
  label: string;
  value: string;
  indent?: boolean;
}) {
  return (
    <Flex justify="space-between" align="center">
      <Text
        type="secondary"
        style={{ fontSize: 12, paddingLeft: indent ? 16 : 0 }}>
        {indent ? `· ${label}` : label}
      </Text>
      <Text style={{ fontSize: 14, fontWeight: 500 }}>{value}</Text>
    </Flex>
  );
}

export default function ReceiptPage() {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const unit = t('billing.usage.currencyUnit');
  const [searchParams] = useSearchParams();
  const paymentNo = searchParams.get('paymentNo') ?? '';

  const { data: response, isLoading } = useReceiptQuery(paymentNo);
  const header = response?.header;
  const receipt = response?.result?.content;

  const sumByType = useMemo(() => {
    const init = { discount: 0, additional: 0 };
    if (!receipt?.extraCharges) return init;
    return receipt.extraCharges.reduce(
      (acc, item) => {
        acc[item.chargeType] += item.chargeAmount;
        return acc;
      },
      { ...init }
    );
  }, [receipt]);

  const filterByType = (type: ExtraCharge['chargeType']) =>
    receipt?.extraCharges.filter((c) => c.chargeType === type) ?? [];

  const handlePrint = () => {
    window.print();
  };

  const handleOpenSalesSlip = () => {
    if (receipt?.salesSlipUrl) {
      window.open(receipt.salesSlipUrl, '_blank');
    }
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: 400 }}>
        <Spin />
      </Flex>
    );
  }

  if (!receipt || !header?.isSuccessful) {
    const errorMessage = (() => {
      switch (header?.resultCode) {
        case ReceiptErrorCode.PAYMENT_NOT_FOUND:
          return t('billing.payment.error.paymentNotFound');
        case ReceiptErrorCode.NOT_OWNED_BY_MEMBER:
          return t('billing.payment.error.receiptNotOwnedByMember');
        case ReceiptErrorCode.NOT_COMPLETED:
          return t('billing.payment.error.receiptNotCompleted');
        case 400:
          return t('billing.payment.error.invalidPaymentNo');
        default:
          return t('billing.payment.error.serverError');
      }
    })();

    return (
      <Flex
        justify="center"
        align="center"
        style={{ minHeight: 400, padding: 24 }}>
        <Text type="secondary">{errorMessage}</Text>
      </Flex>
    );
  }

  return (
    <Flex vertical gap={12} style={{ maxWidth: 800, margin: '0 auto' }}>
      <Flex vertical gap={32} style={{ padding: '24px 28px 0' }}>
        <Flex vertical gap={28}>
          <Flex justify="flex-start">
            <Image
              src="/images/logo-horizontal.svg"
              alt="Contiple"
              preview={false}
              height={24}
            />
          </Flex>

          <Flex vertical gap={20}>
            <Flex vertical gap={12}>
              <ReceiptRow
                label={t('billing.payment.receiptPaymentItem')}
                value={formatPaymentItemName(receipt.itemName, t)}
              />
              <ReceiptRow
                label={t('billing.payment.receiptPaymentMethod')}
                value={getPaymentMethodLabel(receipt.paymentMethod, t)}
              />
              <ReceiptRow
                label={t('billing.payment.receiptPaymentStatus')}
                value={t(
                  `billing.payment.paymentStatusMap.${receipt.paymentStatus}`,
                  { defaultValue: receipt.paymentStatusName }
                )}
              />
              <ReceiptRow
                label={t('billing.payment.receiptPaymentDate')}
                value={receipt.paymentDate}
              />
            </Flex>

            <Divider style={{ margin: 0 }} />

            <Flex vertical gap={12}>
              <ReceiptRow
                label={t('billing.payment.receiptUsageFee', {
                  period: formatPaymentItemName(receipt.chargeName, t),
                })}
                value={`${formatCurrency(receipt.chargedAmount)} ${unit}`}
              />

              <ReceiptRow
                label={t('billing.payment.receiptSurchargeFee')}
                value={`${formatCurrency(sumByType.additional)} ${unit}`}
              />
              {filterByType('additional').map((item, idx) => (
                <ReceiptRow
                  key={`additional-${idx}`}
                  label={item.reason}
                  value={`${formatCurrency(item.chargeAmount)} ${unit}`}
                  indent
                />
              ))}

              <ReceiptRow
                label={`C. ${t('billing.usage.discountFee')}`}
                value={`${formatCurrency(sumByType.discount)} ${unit}`}
              />
              {filterByType('discount').map((item, idx) => (
                <ReceiptRow
                  key={`discount-${idx}`}
                  label={item.reason}
                  value={`${formatCurrency(item.chargeAmount)} ${unit}`}
                  indent
                />
              ))}
            </Flex>

            <Divider style={{ margin: 0 }} />

            <Flex vertical gap={12}>
              <ReceiptRow
                label={t('billing.payment.receiptSubtotal')}
                value={`${formatCurrency(receipt.subtotal)} ${unit}`}
              />
              <ReceiptRow
                label={t('billing.payment.receiptVat')}
                value={`${formatCurrency(receipt.vat)} ${unit}`}
              />
              <ReceiptRow
                label={t('billing.payment.receiptOverdueFee')}
                value={`${formatCurrency(receipt.previousUnpaidAmount ?? 0)} ${unit}`}
              />
            </Flex>

            <Divider style={{ margin: 0 }} />

            <Flex justify="space-between" align="center">
              <Text type="secondary" style={{ fontSize: 16, fontWeight: 600 }}>
                {t('billing.payment.receiptTotalWithOverdue')}
              </Text>
              <Text style={{ fontSize: 18, fontWeight: 700 }}>
                {`${formatCurrency(receipt.total)}${unit}`}
              </Text>
            </Flex>
          </Flex>
        </Flex>

        <Flex
          vertical
          gap={4}
          style={{
            backgroundColor: token.colorFillTertiary,
            padding: '12px 16px',
            borderRadius: 8,
          }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            · {t('billing.payment.receiptNotice1')}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            · {t('billing.payment.receiptNotice2')}
          </Text>
        </Flex>
      </Flex>

      <Flex
        justify="flex-end"
        gap={8}
        style={{
          padding: '24px 32px',
          backgroundColor: token.colorBgElevated,
        }}>
        <Button disabled={!receipt.salesSlipUrl} onClick={handleOpenSalesSlip}>
          {t('billing.payment.receiptCreditCardSlip')}
        </Button>
        <Button
          type="primary"
          icon={<Icons.Printer size={16} />}
          onClick={handlePrint}>
          {t('billing.payment.receiptPrint')}
        </Button>
      </Flex>
    </Flex>
  );
}
